/* ============================================================
   CarePlix OneHealth — Fit-for-Work Readiness Screening
   Real CarePlix rPPG face scan → real measured vitals.
   This config holds the scan parameters and the (illustrative)
   readiness verdict derived from the REAL vitals.
   Guardrail: BP is only ever a risk band, never a mmHg number.
   ============================================================ */

export const VERDICT = { CLEAR: "clear", CAUTION: "caution", REFER: "refer" };

/* Real face-scan parameters */
export const SCAN_DURATION_SEC = 30;                // rPPG capture length (seconds)
export const SCAN_CLIENT = "lt_poc_careplix";       // add-scan notes.client — MUST match the client the CarePlix key is registered under

/* Pulmo (lung) audio-capture lengths (ms). CarePlix needs 44.1kHz mono 16-bit WAV. */
export const PULMO_COUGH_MS = 10000;                // cough recording length
export const PULMO_VOWEL_MS = 6000;                 // sustained vowel-A recording length

/* Adapted PVT-B (Psychomotor Vigilance Task — Brief) attention test. */
export const PVTB_TRIALS = 12;                      // number of reaction-time trials
export const PVTB_ISI_MIN_MS = 1500;               // min random wait before stimulus
export const PVTB_ISI_MAX_MS = 4000;               // max random wait before stimulus
/* Lapse threshold. The classic PVT-B uses 355-500 ms, but on a shared kiosk with
   gloved hands and a resistive-feeling touch panel that produced lapses for
   people who were plainly alert. 950 ms is the site standard here: it catches
   genuine attention failures rather than penalising the input hardware. */
export const PVTB_LAPSE_MS = 950;

/* Mean reaction time bands. 900 ms is a normal tap on this panel; only above
   950 ms is it worth flagging, and 1050 ms is the referral line. */
export const PVTB_MEAN_AMBER_MS = 950;
export const PVTB_MEAN_RED_MS = 1050;

/* Site attention-flag threshold by worker age. */
export function attentionFlagThreshold(age) {
  const years = Number(age);
  if (!Number.isFinite(years)) return 950;
  if (years < 35) return 950;
  if (years <= 45) return 1050;
  return 1200;
}

/* A "lapse" must sit well ABOVE the normal response, otherwise ordinary
   variance around a 900 ms mean makes half the trials lapses — which is exactly
   what dragged good scores down. 950 ms is the reporting threshold the site
   asked for; 1500 ms is what actually counts as an attention failure. */
export const PVTB_SEVERE_LAPSE_MS = 1500;

/* "under 90 seconds" copy anchor only — there is no on-screen clock. */
export const BUDGET_SEC = 90;

/* Simulated employee directory. There is no real face-recognition backend —
   identity is illustrative for the demo. Each record carries the screening flags
   that decide whether the worker also does a Pulmo (lung) scan and/or the
   cognitive (PVT-B attention) test after the face scan. */
export const DIRECTORY = [
  { name: "Ravi Kumar",   badge: "LT-4821", role: "General Labour",     dept: "Fabrication Yard", age: 34, height: 172, weight: 70, pulmoRequired: false, cognitiveRequired: false },
  { name: "Anita Sharma", badge: "LT-3390", role: "Painter / Blaster",  dept: "Paint Shop",       age: 29, height: 160, weight: 58, pulmoRequired: true,  cognitiveRequired: false },
  { name: "Mohammed Ali", badge: "LT-5107", role: "Vehicle Operator",   dept: "Logistics",        age: 41, height: 175, weight: 82, pulmoRequired: false, cognitiveRequired: true  },
  { name: "Suresh Patel", badge: "LT-2764", role: "High-Risk Operator", dept: "Heavy Equipment",  age: 37, height: 168, weight: 74, pulmoRequired: true,  cognitiveRequired: true  },
];
export const pickEmployee = () => DIRECTORY[Math.floor(Math.random() * DIRECTORY.length)];

/* ── Display metadata for the detailed report ──
   The verdict, the BP band and every risk status are computed SERVER-SIDE
   (report.js) and delivered as a sanitized report { verdict, wellbeing,
   params:[{ key, value | band, status }] }. This map only holds how to LABEL
   and describe each parameter for display — never the thresholds or raw BP. */
export const PARAM_META = {
  hr:         { labelKey: "scan.hr",           unit: "bpm",   range: "60–100" },
  resp:       { labelKey: "scan.resp",         unit: "/min",  range: "12–20" },
  spo2:       { labelKey: "scan.spo2",         unit: "%",     range: "95–100" },
  sdnn:       { labelKey: "scan.sdnn",         unit: "ms",    range: "≥ 60" },
  rmssd:      { labelKey: "scan.rmssd",        unit: "ms",    range: "≥ 40" },
  pnn50:      { labelKey: "scan.pnn50",        unit: "%",     range: "≥ 10" },
  cardiacOut: { labelKey: "result.pCardiacOut", unit: "L/min", range: "4–8" },
  bp: {
    labelKey: "scan.bp", band: true, range: "< 120 / 60–79",
    bandLabelKey: {
      excellent: "result.bpExcellent", good: "result.bpGood", average: "result.bpAverage",
      fair: "result.bpFair", poor: "result.bpPoor",
    },
  },
  map: {
    labelKey: "result.pMap", band: true, range: "60–100 mmHg",
    bandLabelKey: { low: "scan.lvlLow", normal: "scan.lvlNormal", high: "scan.lvlHigh" },
  },
  stress: {
    labelKey: "scan.stress", band: true, range: null,
    bandLabelKey: { normal: "scan.lvlNormal", elevated: "scan.lvlElevated", high: "scan.lvlHigh" },
  },
  /* Metabolic / physiological extras (shown only when the scan returns them). */
  hba1c: {
    labelKey: "result.pHba1c", band: true, range: "< 5.7 %",
    bandLabelKey: { optimal: "result.hba1cOptimal", aboveOptimal: "result.hba1cAbove" },
  },
  diabRisk:    { labelKey: "result.pDiabRisk",    unit: "/100",      range: "0–40" },
  bmi:         { labelKey: "result.pBmi",         unit: "",          range: "18.5–24.9" },
  bmr:         { labelKey: "result.pBmr",         unit: "kcal/day",  range: null },
  bloodVolume: { labelKey: "result.pBloodVolume", unit: "mL",        range: null },
  bodyWater:   { labelKey: "result.pBodyWater",   unit: "%",         range: null },
  bodyFat:     { labelKey: "result.pBodyFat",     unit: "%",         range: null },
  vo2max:      { labelKey: "result.pVo2max",      unit: "mL/kg/min", range: null },
};

/* Score → RAG status (headline scores + wellbeing). */
export const scoreStatus = (v) => (v == null ? "na" : v >= 60 ? "green" : v >= 40 ? "amber" : "red");

/* ── Combined readiness verdict across ALL assessments done ──
   Weighted blend of whichever assessments ran (1, 2 or 3):
     face scan (vitals verdict from the server)  weight 0.5  — highest
     Pulmo screening score                        weight 0.3  — second
     Alertness (PVT-B) score                      weight 0.2  — lowest
   Weights are renormalised over the assessments actually completed.
   Each assessment maps to a severity (clear/green = 0 · caution/amber = 1 ·
   refer/red = 2); the weighted severity is thresholded back to a verdict.
   Safety rule: a face-scan REFER is never diluted — it always wins.
   Illustrative screening logic, not a diagnosis. */
export function combineVerdict({ faceVerdict, pulmoScore, alertness }) {
  const faceSev = faceVerdict === VERDICT.REFER ? 2 : faceVerdict === VERDICT.CAUTION ? 1 : 0;
  if (faceSev === 2) return VERDICT.REFER; // vitals red flags are safety-critical

  const statusSev = { green: 0, amber: 1, red: 2 };
  const parts = [{ w: 0.5, s: faceSev }];
  if (pulmoScore != null) parts.push({ w: 0.3, s: statusSev[scoreStatus(pulmoScore)] });
  if (alertness != null) parts.push({ w: 0.2, s: statusSev[scoreStatus(alertness)] });

  const totalW = parts.reduce((a, p) => a + p.w, 0);
  const sev = parts.reduce((a, p) => a + p.w * p.s, 0) / totalW;
  return sev >= 1.5 ? VERDICT.REFER : sev >= 0.5 ? VERDICT.CAUTION : VERDICT.CLEAR;
}

/* Illustrative Alertness score from the REAL PVT-B measures (mean reaction time,
   lapses, false starts). 0–100; higher is more alert. Screening only. */
export function alertnessScore(pvt, age) {
  if (!pvt || !pvt.count) return null;

  const valid = Array.isArray(pvt.rts)
    ? pvt.rts.filter((r) => Number.isFinite(r) && r > 0)
    : [];
  const mean = valid.length
    ? valid.reduce((a, b) => a + b, 0) / valid.length
    : Number(pvt.mean) || 0;
  if (!(mean > 0)) return null;

  /* MEAN reaction time drives the score, on the same anchors as the bands, so
     the number and the colour can never disagree:

        <= 400 ms  100      exceptional
       400-950 ms  100 - 72 normal working range (900 ms is fine)
      950-1050 ms   72 - 50 amber
     1050-1600 ms   50 - 12 red
        > 1600 ms        10

     Earlier versions scored lapse COUNT first. With a 950 ms lapse line and a
     900 ms mean, ordinary variance made half the trials "lapses" and a perfectly
     alert worker scored in the thirties. */
  const flagThreshold = attentionFlagThreshold(age);
  const amberThreshold = Math.max(400, flagThreshold - 100);
  let base;
  if (mean <= 400) base = 100;
  else if (mean <= amberThreshold) base = 100 - 28 * ((mean - 400) / (amberThreshold - 400));
  else if (mean <= flagThreshold) base = 72 - 22 * ((mean - amberThreshold) / (flagThreshold - amberThreshold));
  else if (mean <= 1600) base = 50 - 38 * ((mean - flagThreshold) / (1600 - flagThreshold));
  else base = 10;

  /* Genuine attention failures — responses beyond 1500 ms — cost on top of the
     mean, because a worker can hold a decent average while blanking twice. */
  const severe = valid.filter((r) => r >= PVTB_SEVERE_LAPSE_MS).length;
  const severePenalty = valid.length
    ? 15 * Math.min(1, (severe / valid.length) / 0.25)
    : 0;

  // Taps before the stimulus: impulsivity, and the usual way of gaming the test.
  const fsPenalty = 10 * Math.min(1, (pvt.falseStarts || 0) / 3);

  return Math.max(3, Math.min(100, Math.round(base - severePenalty - fsPenalty)));
}
